import { ReactElement, useEffect, useState } from 'react';
import Loader from 'components/Loader';
import { useAppSelector } from 'helpers/hooks';
import { getActiveRole } from 'selectors/admins';
import { has_permission } from 'config/permissions';
import { CustomTable, tableButton } from 'components/CustomTable';
import { adminColumns } from 'constants/tables';
import { PagesFilters } from 'components/Filters/PagesFilters';
import { useNavigate } from 'react-router-dom';
import { ModalComponent } from 'components/Modal';
import { getResourcesFromResourceAccess } from 'helpers/utils';
import { useGetAdminUsersListQuery } from 'services/adminsApi';
import { datadogLogs } from '@datadog/browser-logs';

interface IData {
  id: number;
  name?: string;
  roleType?: string;
  roleTypes?: string | string[];
  clientName?: string | string[];
  status?: string;
  actions?: ReactElement;
}

export const AdminsList = () => {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<IData[]>([]);
  const [filterSelected, setFilterSelected] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const activeRole = useAppSelector(getActiveRole);

  const closePopup = () => setIsOpen(false);

  // Use RTK Query hook to fetch admin users
  const {
    data: adminsData = [],
    isLoading,
    isFetching,
  } = useGetAdminUsersListQuery({ search_str: '' }, { skip: !activeRole });

  const createAdmin = () => {
    navigate('/admins_detail');
  };

  // commenting while unused
  const handleUpdate = (type: string, id: string) => {
    if (type === 'edit') navigate(`/admins_detail?admin_id=${id}`);
    if (type === 'delete') setIsOpen(true);
  };

  const handleDelete = () => {
    // TODO: Delete admin from backend
    datadogLogs.logger.debug('Delete admin action triggered - currently a no-op');
  };

  useEffect(() => {
    if (!isLoading && !isFetching && adminsData?.length > 0) {
      setDataState(
        adminsData
          .filter(admin => (filterSelected === '' ? true : admin.admin_status === filterSelected))
          .map((prop, key) => {
            return {
              id: key,
              name: `${prop.first_name} ${prop.last_name}`,
              email: prop.email,
              roleTypes: prop.role_names,
              clientName: getResourcesFromResourceAccess(prop.resource_access),
              status: prop.admin_status,
              actions: (
                <div className="grid-column">
                  <div className="table-action-row">
                    {has_permission('ADMINS_ROUTE', 'U', activeRole) &&
                      tableButton(handleUpdate, 'edit', prop.id)}
                  </div>
                  {/* <div className="table-action-row margin0">
                    {has_permission('ADMINS_ROUTE', 'D') &&
                      tableButton(handleUpdate, 'delete', prop.admin_id)}
                  </div> */}
                </div>
              ),
            };
          })
      );
    }
  }, [isLoading, isFetching, adminsData, filterSelected, activeRole]);

  return (
    <>
      <ModalComponent isOpen={isOpen} closeModal={closePopup}>
        <div className="grid-column">
          <span className="oath-eyebrow inkBlue">Delete Admin</span>
          <span className="oath-body inkBlue">
            Are you sure you would like to delete this admin? This can&apos;t be undone.
          </span>
          <button type="button" className="btn-inkBlue btn btn-secondary" onClick={handleDelete}>
            Delete
          </button>
          <button
            type="button"
            className="oath-bodysmall link-text gray padding0"
            onClick={closePopup}
          >
            Cancel
          </button>
        </div>
      </ModalComponent>
      {(isLoading || isFetching) && <Loader type="dots" />}
      {!has_permission('ADMINS_ROUTE', 'R', activeRole) ? (
        <div className="content-full-width">
          <h1 className="title-permission">
            Sorry, you don&apos;t have permission to see this screen.
          </h1>
        </div>
      ) : (
        <>
          <div className="row filters-row">
            <PagesFilters setFilterSelected={setFilterSelected} allValue="Admins" />
          </div>
          <div className="content-full-width">
            <div className="row custom-header-container">
              <div className="grid-column col-lg-9 col-md-8 col-sm-12 align-center">
                <span className="oath-h3 inkBlue">Oath Admins</span>
                <span className="oath-bodysmall inkBlue">{adminsData?.length} Total</span>
              </div>
              <div className="new-page-button col-lg-3 col-md-4 col-sm-12">
                <button
                  type="button"
                  className="btn-inkBlue btn btn-secondary btn-block"
                  onClick={createAdmin}
                >
                  Create New Admin
                </button>
              </div>
            </div>
            <div className="row recent-donations-table">
              <div className="col-lg-12">
                <CustomTable
                  key="admins-list"
                  title="Admins"
                  data={dataState}
                  columns={adminColumns}
                  withPagination
                  screen="admins"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
